<template>
  <div
    ref="observedElement"
    class="component-observer"
  >
    <transition
      :name="transitionProp.name"
      :mode="transitionProp.mode"
    >
      <div
        v-if="llMixin_componentIsLoading"
        key="loader"
        class="component-observer-loader"
      >
        <!-- custom loading content, optional -->
        <slot
          name="loading-content"
        >
          <loading-spinner
            :placeholder-style="placeholderStyle"
          />
        </slot>
      </div>
      <div
        v-else
        key="component"
        class="component-observer-body"
        :style="bodyStyle"
      >
        <!-- default slot taking the template that should load when user scrolls by -->
        <slot />
      </div>
    </transition>
  </div>
</template>

<script>
import LoadingSpinner from 'components/loading/LoadingSpinner.vue';
import LoadingLogic from 'mixins/LoadingLogic';

/**
 * @@author         j0Shi
 * @@date           2019-09-17
 * @@description    Loads components based on IntersectionObserver API
 * @@usedby         Object
 *
 * from https://alligator.io/vuejs/lazy-image/
 */
export default {
  name: 'ComponentObserver',
  components: {
    LoadingSpinner,
  },
  mixins: [LoadingLogic],
  props: {
    // when not using custom loading slot, you can define the placeholder CSS here
    placeholderStyle: {
      type: Object,
      default: () => ({ height: '100px' }),
    },
    // set a custom rootMargin for the Intersection API
    rootMargin: {
      type: String,
      default: '250px',
    },
    // add custom style to the component oberserver body
    bodyStyle: {
      type: Object,
      default: () => ({}),
    },
    // transition when switching between loading placeholder and the lazy-loaded component
    transitionProp: {
      type: Object,
      required: false,
      default: () => ({
        name: 'fade',
        mode: 'out-in',
      }),
    },
    // if true don't render the component immediately when in viewport, but during the next idle state
    useIdleCallback: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    observer: null,
    intersected: false,
    idle: () => {},
  }),
  computed: {
    componentInvisible() {
      return !this.intersected;
    },
  },
  created() {
    this.llMixin_addComponentLoadingIndicator('componentInvisible');
  },
  mounted() {
    this.observer = new IntersectionObserver(this.intersectCallback, {
      rootMargin: this.rootMargin,
    });

    this.observer.observe(this.$refs.observedElement);
  },
  beforeDestroy() {
    this.idle();
    this.observer.disconnect();
  },
  methods: {
    resolve() {
      this.intersected = true;
      // emits a ping when the component was loaded
      this.$emit('component-intersected', true);
      this.observer.disconnect();
    },
    intersectCallback(entries) {
      const el = entries[0];
      if (el.isIntersecting) {
        if (!this.useIdleCallback || !('requestIdleCallback' in window) || !('requestAnimationFrame' in window)) {
          this.resolve();
        } else {
          const self = this;
          const id = requestIdleCallback(() => {
            requestAnimationFrame(() => {
              self.resolve();
            });
          });
          this.idle = () => cancelIdleCallback(id);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  $color1: #e1e1e1;
  $color2: #909090;

  .component-observer {
    min-height: 0.5em; // observer with 0px height sometimes not triggered on ssr
  }
</style>
